'use client'

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'

import type { ClientConfig } from '@/config/schema'
import { initializeClientEnv, type ClientEnv, type ClientEnvServiceFactories } from '@/env/client'

const ClientEnvContext = createContext<ClientEnv | null>(null)

export interface ClientEnvProviderProps {
    config: ClientConfig
    /** Optional service factories for dependency injection (testing or swapping implementations) */
    factories?: ClientEnvServiceFactories
    children: ReactNode
}

/**
 * Provider component that initializes and provides ClientEnv to child components.
 *
 * Construction is synchronous via `useMemo`. The browser-only services
 * (logger / Datadog) defer SSR-safety to the hand-written sentinel inside
 * `initializeClientLogger` / `initializeDatadog`, so the
 * provider does NOT need a useState/useEffect deferral.
 *
 * Mounted by the locale layout.
 *
 * @param config - Client configuration
 * @param factories - Optional custom factories for creating services
 */
export function ClientEnvProvider({ config, factories, children }: ClientEnvProviderProps) {
    const env = useMemo(() => initializeClientEnv(config, factories), [config, factories])

    // Single post-mount diagnostic — confirms the provider hydrated. Real
    // service init already ran synchronously in useMemo.
    useEffect(() => {
        env.logger.getLogger({ module: 'client-env-provider' }).debug('ClientEnvProvider mounted')
    }, [env])

    return <ClientEnvContext.Provider value={env}>{children}</ClientEnvContext.Provider>
}

/**
 * Hook to access the ClientEnv from client components.
 *
 * Returns a non-null `ClientEnv`. Throws if called outside a
 * `ClientEnvProvider`. Browser-only service METHOD calls
 * during SSR fall through to the sentinel and report misuse via
 * `reportSSRMisuse`.
 *
 * @throws Error if used outside ClientEnvProvider
 */
export function useClientEnv(): ClientEnv {
    const env = useContext(ClientEnvContext)
    if (!env) {
        throw new Error(
            'useClientEnv must be used within a ClientEnvProvider. Mount the ' +
                'provider in your locale layout (see src/app/[locale]/layout.tsx).',
        )
    }
    return env
}

/**
 * Hook to access client config. Equivalent to `useClientEnv().config`.
 *
 * @throws Error if used outside ClientEnvProvider
 */
export function useClientConfig(): ClientConfig {
    return useClientEnv().config
}
